import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  Save,
  RefreshCw,
  X
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import api from '../../../api/axios';
import { toast } from 'react-hot-toast';
import { useLoading } from '../../../context/LoadingContext';
import styles from './PayrollForm.module.css';

const EditPayroll = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [formData, setFormData] = useState({
    other_deduction: 0,
    other_deduction_remarks: '',
    remarks: '',
    deduct_loan: false,
    loan_emi_deduction: 0
  });
  
  const [payrollData, setPayrollData] = useState(null);
  const [activeLoan, setActiveLoan] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchPayroll();
  }, [id]);

  const fetchPayroll = async () => {
    showLoader();
    try {
      const response = await api.get(`/payroll/${id}`);
      if (response.data.success) {
        const data = response.data.data;
        if (data.status !== 'draft' && data.status !== 'generated') {
          toast.error('Only draft or generated payrolls can be edited');
          navigate('/employees/payroll');
          return;
        }
        setPayrollData(data);
        if (response.data.meta && response.data.meta.active_loan) {
            setActiveLoan(response.data.meta.active_loan);
        }
        setFormData({
          other_deduction: data.other_deduction || 0,
          other_deduction_remarks: data.other_deduction_remarks || '',
          remarks: data.remarks || '',
          deduct_loan: parseFloat(data.loan_emi_deduction) > 0,
          loan_emi_deduction: parseFloat(data.loan_emi_deduction) || 0
        });
      }
    } catch (error) {
      toast.error('Failed to load payroll details');
      navigate('/employees/payroll');
    } finally {
      hideLoader();
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'loan_emi_deduction' && activeLoan) {
        const val = parseFloat(value) || 0;
        if (val > activeLoan.remaining_amount) {
            toast.error(`Cannot exceed remaining loan amount (₹${activeLoan.remaining_amount})`);
            return;
        }
    }

    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    showLoader();
    try {
      const payload = {
        ...formData,
        loan_emi_deduction: formData.deduct_loan ? parseFloat(formData.loan_emi_deduction) || 0 : 0
      };
      const response = await api.put(`/payroll/${id}`, payload);
      if (response.data.success) {
        toast.success('Payroll updated successfully');
        navigate('/employees/payroll');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update payroll');
    } finally {
      setIsUpdating(false);
      hideLoader();
    }
  };

  if (!payrollData) return null;

  return (
    <div className={`${styles['create-po-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      
      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />
        
        <main className={styles['main-content']}>
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <Breadcrumbs customItems={[
                { label: 'HR', path: '#' },
                { label: 'Payroll / Salary', path: '/employees/payroll' },
                { label: 'Edit Payroll', path: '#' }
              ]} />
              <h1>Edit Payroll: {payrollData.payroll_number}</h1>
              <p>Update deductions for {payrollData.employee?.employee_name}</p>
            </div>
            
            <div className={styles['header-actions-btns']}>
              <button type="button" onClick={() => navigate('/employees/payroll')} className={styles['btn-cancel']}>
                <X size={18} /> <span>Cancel</span>
              </button>
              <button type="button" onClick={handleUpdate} className={styles['btn-primary']} disabled={isUpdating}>
                {isUpdating ? <RefreshCw size={18} className="spin" /> : <Save size={18} />}
                <span>Update Payroll</span>
              </button>
            </div>
          </div>

          <div className={styles['form-sections-grid']}>
            {/* General Details */}
            <div className={styles['form-card']}>
              <div className={styles['card-header-po']}>
                <h3>Update Deductions</h3>
              </div>
              <div className={styles['card-content']}>
                <div className={styles['form-grid-2']}>
                  
                  <div className={styles['form-group']} style={{ gridColumn: 'span 2' }}>
                    <label>Employee</label>
                    <input 
                      type="text" 
                      value={`${payrollData.employee?.employee_name} (${payrollData.employee?.employee_code})`} 
                      disabled
                      className={styles['form-input']}
                      style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed' }}
                    />
                  </div>

                  {activeLoan && (
                    <div className={styles['form-group']} style={{ gridColumn: 'span 2', display: 'flex', gap: '20px', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input 
                          type="checkbox" 
                          id="deduct_loan"
                          name="deduct_loan"
                          checked={formData.deduct_loan}
                          onChange={handleChange}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <label htmlFor="deduct_loan" style={{ margin: 0 }}>Deduct Loan EMI?</label>
                      </div>
                      
                      {formData.deduct_loan && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                          <div style={{ position: 'relative', width: '200px' }}>
                            <span style={{ position: 'absolute', left: '12px', top: '10px', color: '#64748b' }}>₹</span>
                            <input 
                              type="number" 
                              name="loan_emi_deduction" 
                              value={formData.loan_emi_deduction} 
                              onChange={handleChange} 
                              className={styles['form-input']}
                              min="0"
                              max={activeLoan.remaining_amount}
                              style={{ paddingLeft: '32px' }}
                            />
                          </div>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            Max: ₹{activeLoan.remaining_amount} (Default: ₹{activeLoan.emi_amount})
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className={styles['form-group']}>
                    <label>Other Deduction Amount</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '12px', top: '10px', color: '#64748b' }}>₹</span>
                      <input 
                        type="number" 
                        name="other_deduction" 
                        value={formData.other_deduction} 
                        onChange={handleChange} 
                        className={styles['form-input']}
                        min="0"
                        style={{ paddingLeft: '32px' }}
                      />
                    </div>
                  </div>
                  
                  <div className={styles['form-group']}>
                    <label>Other Deduction Remarks</label>
                    <input 
                      type="text" 
                      name="other_deduction_remarks" 
                      value={formData.other_deduction_remarks} 
                      onChange={handleChange} 
                      className={styles['form-input']}
                      placeholder="e.g., Canteen charges"
                    />
                  </div>

                  <div className={styles['form-group']} style={{ gridColumn: 'span 2' }}>
                    <label>Remarks / Notes</label>
                    <textarea 
                      name="remarks" 
                      value={formData.remarks} 
                      onChange={handleChange} 
                      className={styles['form-textarea']}
                      rows="3"
                      placeholder="Additional notes for this payroll..."
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles['form-card']}>
              <div className={styles['card-header-po']}>
                <h3>Payroll Breakdown</h3>
              </div>
              
              <div className={styles['card-content']}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  {/* Attendance Highlights */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                    <div style={{ backgroundColor: '#f1f5f9', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Total Days</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#0f172a' }}>{payrollData.total_days_in_month || 0}</div>
                    </div>
                    <div style={{ backgroundColor: '#ecfdf5', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#059669', marginBottom: '4px' }}>Present</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#047857' }}>{payrollData.present_days || 0}</div>
                    </div>
                    <div style={{ backgroundColor: '#fef2f2', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#e11d48', marginBottom: '4px' }}>Leave/Absent</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#be123c' }}>{payrollData.absent_days || 0}</div>
                    </div>
                    <div style={{ backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#16a34a', marginBottom: '4px' }}>OT Days</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#15803d' }}>{payrollData.ot_days || 0}</div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                    {/* Earnings */}
                    <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <h4 style={{ margin: '0 0 16px 0', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>Earnings</h4>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#475569' }}>Base Salary</span>
                        <span style={{ fontWeight: '500' }}>₹{payrollData.basic_salary || 0}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#475569' }}>Per Day Salary</span>
                        <span style={{ fontWeight: '500' }}>₹{payrollData.per_day_salary || 0}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#475569' }}>Earned Salary ({payrollData.working_days || 0} days)</span>
                        <span style={{ fontWeight: '500' }}>₹{payrollData.earned_salary || 0}</span>
                      </div>
                      {payrollData.ot_amount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#475569' }}>Overtime ({payrollData.ot_days} days)</span>
                          <span style={{ fontWeight: '500' }}>₹{payrollData.ot_amount || 0}</span>
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed #cbd5e1' }}>
                        <span style={{ color: '#0f172a', fontWeight: '600' }}>Gross Salary</span>
                        <span style={{ color: '#059669', fontWeight: '700' }}>₹{payrollData.gross_salary || 0}</span>
                      </div>
                    </div>
                    
                    {/* Deductions */}
                    <div style={{ padding: '16px', backgroundColor: '#fef2f2', borderRadius: '12px', border: '1px solid #fecaca' }}>
                      <h4 style={{ margin: '0 0 16px 0', color: '#991b1b', borderBottom: '1px solid #fecaca', paddingBottom: '8px' }}>Deductions</h4>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#7f1d1d' }}>PF</span>
                        <span style={{ fontWeight: '500' }}>₹{payrollData.pf_deduction || 0}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#7f1d1d' }}>ESIC</span>
                        <span style={{ fontWeight: '500' }}>₹{payrollData.esic_deduction || 0}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#7f1d1d' }}>Professional Tax</span>
                        <span style={{ fontWeight: '500' }}>₹{payrollData.professional_tax || 0}</span>
                      </div>
                      {payrollData.advance_deduction > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#7f1d1d' }}>Advance Deducted</span>
                          <span style={{ fontWeight: '500' }}>₹{payrollData.advance_deduction}</span>
                        </div>
                      )}
                      {payrollData.loan_emi_deduction > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#7f1d1d' }}>Loan EMI</span>
                          <span style={{ fontWeight: '500' }}>₹{payrollData.loan_emi_deduction}</span>
                        </div>
                      )}
                      {(Number(formData.other_deduction) || 0) >= 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#7f1d1d' }}>Other Deductions</span>
                          <span style={{ fontWeight: '500' }}>₹{Number(formData.other_deduction) || 0}</span>
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed #fca5a5' }}>
                        <span style={{ color: '#991b1b', fontWeight: '600' }}>Total Deductions</span>
                        <span style={{ color: '#dc2626', fontWeight: '700' }}>
                          ₹{
                            (Number(payrollData.pf_deduction) || 0) + 
                            (Number(payrollData.esic_deduction) || 0) + 
                            (Number(payrollData.professional_tax) || 0) + 
                            (Number(payrollData.advance_deduction) || 0) + 
                            (Number(payrollData.loan_emi_deduction) || 0) + 
                            (Number(formData.other_deduction) || 0)
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Net Total */}
                  <div style={{ padding: '24px', backgroundColor: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px 0', color: '#1e3a8a', fontSize: '18px' }}>Net Payable Salary</h3>
                      <p style={{ margin: 0, color: '#3b82f6', fontSize: '14px' }}>Gross Salary - Total Deductions</p>
                    </div>
                    <span style={{ fontSize: '32px', fontWeight: '800', color: '#1d4ed8' }}>
                      ₹{
                        Math.max(0, (Number(payrollData.gross_salary) || 0) - (
                          (Number(payrollData.pf_deduction) || 0) + 
                          (Number(payrollData.esic_deduction) || 0) + 
                          (Number(payrollData.professional_tax) || 0) + 
                          (Number(payrollData.advance_deduction) || 0) + 
                          (Number(payrollData.loan_emi_deduction) || 0) + 
                          (Number(formData.other_deduction) || 0)
                        )).toLocaleString('en-IN')
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default EditPayroll;
